import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Star, Clock, Utensils } from 'lucide-react';

export const Houston: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Best Chinese Food in Houston | Chinese Food Maps</title>
        <meta name="description" content="Discover the best Chinese restaurants in Houston. From authentic dim sum in Asiatown to regional specialties in Bellaire, explore Houston's diverse Chinese food scene." />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Best Chinese Food in Houston</h1>

        <div className="prose max-w-none text-gray-600 mb-12">
          <p className="text-lg leading-relaxed">
            Houston's Asiatown, particularly along Bellaire Boulevard, is a thriving hub of authentic Chinese cuisine. The city's diverse Chinese food scene offers everything from traditional Cantonese dim sum to fiery Sichuan specialties, reflecting Houston's multicultural character and sophisticated palate.
          </p>
        </div>

        <h2 className="text-2xl font-bold mb-8 text-black">Top Rated Chinese Food Restaurants</h2>

        <div className="grid gap-8">
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">Houston Asiatown</h2>
            
            <div className="space-y-8">
              {/* Fung's Kitchen */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Fung's Kitchen</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.4</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>7320 Southwest Fwy #115, Houston, TX 77074</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(713) 779-2288</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Traditional Dim Sum</li>
                    <li>Cantonese Seafood</li>
                    <li>Live Tank Selections</li>
                    <li>Banquet Facilities</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Shrimp Har Gow</li>
                    <li>Live Lobster Preparations</li>
                    <li>Peking Duck</li>
                    <li>Egg Custard Tarts</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=7320+Southwest+Fwy+115+Houston+TX+77074`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:7137792288"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Mala Sichuan Bistro */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Mala Sichuan Bistro</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.6</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>9348 Bellaire Blvd, Houston, TX 77036</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(713) 995-1889</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Authentic Sichuan Cuisine</li>
                    <li>House-made Chili Oil</li>
                    <li>Traditional Spices</li>
                    <li>James Beard Nominated</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Water Boiled Fish</li>
                    <li>Red Oil Dumplings</li>
                    <li>Mapo Tofu</li>
                    <li>Dan Dan Noodles</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=9348+Bellaire+Blvd+Houston+TX+77036`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:7139951889"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* One Dragon Restaurant */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">One Dragon Restaurant</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.5</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>9310 Bellaire Blvd, Houston, TX 77036</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(713) 995-9398</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Shanghai Cuisine</li>
                    <li>Soup Dumplings</li>
                    <li>Traditional Breakfast</li>
                    <li>Homestyle Dishes</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Xiao Long Bao</li>
                    <li>Pan-Fried Pork Buns</li>
                    <li>Scallion Pancakes</li>
                    <li>Shanghai Noodles</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=9310+Bellaire+Blvd+Houston+TX+77036`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:7139959398"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Uyghur Bistro */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Uyghur Bistro</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.4</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>9888 Bellaire Blvd #168, Houston, TX 77036</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(832) 795-9259</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Uyghur Cuisine</li>
                    <li>Hand-Pulled Noodles</li>
                    <li>Grilled Meats</li>
                    <li>Northwestern Chinese</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Big Plate Chicken</li>
                    <li>Laghman Noodles</li>
                    <li>Lamb Kebabs</li>
                    <li>Polo (Rice Pilaf)</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=9888+Bellaire+Blvd+168+Houston+TX+77036`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:8327959259"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Golden Dumpling House */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Golden Dumpling House</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.3</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>9896 Bellaire Blvd #H, Houston, TX 77036</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(713) 270-9996</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Northern Chinese Dumplings</li>
                    <li>Hand-Made Noodles</li>
                    <li>Beijing-Style Dishes</li>
                    <li>Quick Service</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Pork & Chive Dumplings</li>
                    <li>Beef Noodle Soup</li>
                    <li>Green Onion Pancakes</li>
                    <li>Spicy Won Tons</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=9896+Bellaire+Blvd+H+Houston+TX+77036`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:7132709996"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">Houston Chinese Food Fun Facts</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Cultural Hub</h3>
                  <p className="text-gray-700">Houston's Asiatown is one of the largest in the Southern United States, with over 100 Chinese restaurants.</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Regional Diversity</h3>
                  <p className="text-gray-700">The area features restaurants specializing in cuisines from all regions of China, including rare specialties like Uyghur cuisine.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Modern Growth</h3>
                  <p className="text-gray-700">Houston's Chinese food scene has expanded beyond traditional Cantonese to include modern interpretations and fusion cuisines.</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Local Innovation</h3>
                  <p className="text-gray-700">Many Houston Chinese restaurants have adapted traditional recipes to incorporate local Gulf Coast ingredients.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">Best Times to Visit</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">Peak Hours</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <Clock size={18} className="text-red-600" />
                    Dim Sum: 10 AM - 2 PM (weekends)
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock size={18} className="text-red-600" />
                    Dinner: 6 PM - 9 PM
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">Special Events</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <Utensils size={18} className="text-red-600" />
                    Lunar New Year Festival (Jan/Feb)
                  </li>
                  <li className="flex items-center gap-2">
                    <Utensils size={18} className="text-red-600" />
                    Asian Night Market (Oct)
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};